import { projects } from '@/projects';
import type { Project } from '@/projects/types';

const SESSION_PREFIX = 'unlocked:';
const TTL_MS = 30 * 60 * 1000;

const getSalt = () => import.meta.env.VITE_UNLOCK_SALT || 'mad73-unlock-salt';

const hashString = async (value: string) => {
  if (!globalThis.crypto?.subtle) return value;
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const buildProof = async (slug: string, method: 'pin' | 'ticket') => {
  const salt = getSalt();
  return hashString(`${slug}:${method}:${salt}`);
};

export const getProjectBySlug = (slug: string) => projects.find((project) => project.slug === slug);

export const verifyPin = (project: Project, pin: string) => project.accessPin === pin;

export const verifyQrTicket = (project: Project, ticket: string) =>
  project.qrTickets.some((value) => value === ticket);

export const setUnlocked = async (slug: string, method: 'pin' | 'ticket') => {
  const proof = await buildProof(slug, method);
  const payload = {
    exp: Date.now() + TTL_MS,
    proof,
  };
  sessionStorage.setItem(`${SESSION_PREFIX}${slug}`, JSON.stringify(payload));
};

export const isUnlocked = async (slug: string) => {
  const raw = sessionStorage.getItem(`${SESSION_PREFIX}${slug}`);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { exp: number; proof: string };
    if (!parsed.exp || !parsed.proof) {
      sessionStorage.removeItem(`${SESSION_PREFIX}${slug}`);
      return false;
    }
    if (Date.now() > parsed.exp) {
      sessionStorage.removeItem(`${SESSION_PREFIX}${slug}`);
      return false;
    }
    const pinProof = await buildProof(slug, 'pin');
    const ticketProof = await buildProof(slug, 'ticket');
    return parsed.proof === pinProof || parsed.proof === ticketProof;
  } catch {
    sessionStorage.removeItem(`${SESSION_PREFIX}${slug}`);
    return false;
  }
};

export const clearUnlocked = (slug: string) => {
  sessionStorage.removeItem(`${SESSION_PREFIX}${slug}`);
};
